/** @jsx jsx */
import { css, jsx } from '@compiled/react';
import { getATLContextUrl } from '@atlaskit/atlassian-context';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Box, Text, xcss } from '@atlaskit/primitives';
import ExclamationTriangle from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/error/assets/exclamation-triangle.svg';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import messages from './messages.tsx';

export const SmallContactSupportWarning = () => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={gadgetStackSmStyles}>
			<Box xcss={imageContainerSmStyles}>
				<Image
					src={ExclamationTriangle}
					alt={formatMessage({ id: 'iconLabel' })}
					css={imageSmStyles}
				/>
			</Box>
			<Text align="start">
				<FormattedMessage
					{...messages.errorMessageWithSupportLinkShort}
					values={{
						heading: (chunks) => (
							<Text maxLines={1}>
								<Heading size="small">{chunks}</Heading>
							</Text>
						),
						text: (chunks) => (
							<Text size="small" maxLines={1}>
								{chunks}
							</Text>
						),
						link: (chunks) => (
							<Link href={`${getATLContextUrl('support')}/contact/#/`} target="_blank">
								{chunks}
							</Link>
						),
					}}
				/>
			</Text>
		</Box>
	);
};

export const MediumContactSupportWarning = () => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={gadgetStackMdStyles}>
			<Box xcss={imageContainerMd}>
				<Image src={ExclamationTriangle} alt={formatMessage({ id: 'iconLabel' })} css={imageMd} />
			</Box>
			<Text align="center">
				<FormattedMessage
					{...messages.errorMessageWithSupportLinkLong}
					values={{
						heading: (chunks) => (
							<Text maxLines={1}>
								<Heading size="small" as="h2">
									{chunks}
								</Heading>
							</Text>
						),
						text: (chunks) => (
							<Text size="medium" maxLines={1}>
								{chunks}
							</Text>
						),
						link: (chunks) => (
							<Link href={`${getATLContextUrl('support')}/contact/#/`} target="_blank">
								{chunks}
							</Link>
						),
					}}
				/>
			</Text>
		</Box>
	);
};

export const SmallRetryWarning = ({ handleRetry }: { handleRetry: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={gadgetStackSmStyles}>
			<Box xcss={imageContainerSmStyles}>
				<Image
					src={ExclamationTriangle}
					alt={formatMessage({ id: 'iconLabel' })}
					css={imageSmStyles}
				/>
			</Box>
			<Text align="start">
				<FormattedMessage
					{...messages.errorMessageWithRefreshButtonShort}
					values={{
						heading: (chunks) => <Heading size="small">{chunks}</Heading>,
						text: (chunks) => (
							<Text size="small" maxLines={1}>
								{chunks}
							</Text>
						),
						button: (chunks) => (
							<Button appearance="subtle" onClick={handleRetry}>
								{chunks}
							</Button>
						),
					}}
				/>
			</Text>
		</Box>
	);
};

export const MediumRetryWarning = ({ handleRetry }: { handleRetry: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={gadgetStackMdStyles}>
			<Box xcss={imageContainerMd}>
				<Image src={ExclamationTriangle} alt={formatMessage({ id: 'iconLabel' })} css={imageMd} />
			</Box>
			<Text align="center">
				<FormattedMessage
					{...messages.errorMessageWithRefreshButtonLong}
					values={{
						heading: (chunks) => (
							<Heading size="small" as="h2">
								{chunks}
							</Heading>
						),
						text: (chunks) => (
							<Text size="medium" maxLines={1}>
								{chunks}
							</Text>
						),
						button: (chunks) => (
							<Button appearance="subtle" onClick={handleRetry}>
								{chunks}
							</Button>
						),
					}}
				/>
			</Text>
		</Box>
	);
};

const gadgetStackSmStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	justifyContent: 'center',
	flexDirection: 'row',
	height: '100%',
});

const imageContainerSmStyles = xcss({
	width: '35px',
	height: '35px',
});

const imageSmStyles = css({
	objectFit: 'contain',
	width: '100%',
	height: '100%',
});

const gadgetStackMdStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	justifyContent: 'center',
	flexDirection: 'column',
	height: '100%',
});

const imageContainerMd = xcss({
	width: '90px',
	height: '90px',
});

const imageMd = css({
	objectFit: 'contain',
	width: '100%',
	height: '100%',
});
